import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Globe, Bot, MapPinned } from "lucide-react";
import { getKeywords, createKeyword, updateKeyword, deleteKeyword, apiErr } from "../lib/api";
import { PageHead, Stat, Modal, Empty, Pill, BtnPrimary, BtnGhost, Field, inputCls, areaCls } from "./_ui";
import { BLOG_POSTS } from "@/data/blog";
import { FESTIVALS } from "@/data/festivals";

const EMPTY = { keyword: "", target_url: "", intent: "SEO", status: "idea", position: "", notes: "" };
const INTENTS = ["SEO", "AEO", "GEO"];
const STATUSES = ["idea", "tracking", "ranking"];

function KwForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.keyword.trim()) return toast.error("Keyword is required");
    const body = { keyword: f.keyword.trim(), target_url: f.target_url, intent: f.intent, status: f.status, notes: f.notes, position: f.position ? Number(f.position) : null };
    setBusy(true);
    try {
      if (f.id) await updateKeyword(f.id, body); else await createKeyword(body);
      toast.success(f.id ? "Keyword updated" : "Keyword added");
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={f.id ? "Edit keyword" : "Track a keyword"} onClose={onClose} testid="kw-form-modal"
      footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost><BtnPrimary onClick={save} disabled={busy} data-testid="kw-form-save">{busy ? "Saving…" : "Save"}</BtnPrimary></>}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Keyword / query" full><input value={f.keyword} onChange={set("keyword")} data-testid="kw-field-keyword" placeholder="e.g. griha pravesha purohit bengaluru" className={inputCls} /></Field>
        <Field label="Target URL" full><input value={f.target_url} onChange={set("target_url")} placeholder="/blog/..." className={inputCls} /></Field>
        <Field label="Intent"><select value={f.intent} onChange={set("intent")} className={inputCls}>{INTENTS.map((i) => <option key={i}>{i}</option>)}</select></Field>
        <Field label="Status"><select value={f.status} onChange={set("status")} className={inputCls}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Position (optional)"><input type="number" value={f.position} onChange={set("position")} className={inputCls} /></Field>
        <Field label="Notes" full><textarea rows={2} value={f.notes} onChange={set("notes")} className={areaCls} /></Field>
      </div>
    </Modal>
  );
}

export default function SearchGrowth() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("All");

  const load = () => getKeywords().then(setRows).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(apiErr(e)); } };

  const filtered = tab === "All" ? rows : rows.filter((r) => r.intent === tab);
  const ranking = rows.filter((r) => r.status === "ranking").length;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-7 md:px-8" data-testid="admin-search-growth">
      <PageHead eyebrow="Discovery" title="Search growth"
        desc="Track the keywords you want to win across SEO, AEO (AI answers) and GEO (local), against the content already live."
        action={<BtnPrimary onClick={() => setForm(EMPTY)} data-testid="kw-add-btn" className="inline-flex items-center gap-1.5"><Plus size={16} /> Add keyword</BtnPrimary>} />

      <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Stat icon={Search} label="Keywords tracked" value={rows.length} />
        <Stat icon={Globe} tint={{ bg: "#EEF2E6", fg: "#6D8B44" }} label="Ranking" value={ranking} />
        <Stat icon={Bot} tint={{ bg: "#F0ECF7", fg: "#6A4EA8" }} label="Indexable content" value={BLOG_POSTS.length + FESTIVALS.length} sub="Blogs + festival guides" />
        <Stat icon={MapPinned} tint={{ bg: "#E6F0EE", fg: "#2F7A6B" }} label="GEO targets" value={rows.filter((r) => r.intent === "GEO").length} />
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {["All", ...INTENTS].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`kw-tab-${t}`}
            className={`rounded-full px-3.5 py-1.5 font-body text-xs font-semibold ${tab === t ? "bg-[#D84315] text-white" : "border border-[#E5DFD3] text-[#5C5449] hover:bg-[#F5F1E8]"}`}>{t}</button>
        ))}
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-[#E9E2D6] bg-white">
        <div className="hidden grid-cols-12 gap-3 border-b border-[#F0EBE1] bg-[#FAFAF5] px-4 py-3 font-body text-[11px] font-semibold uppercase tracking-wide text-[#A79C8A] md:grid">
          <span className="col-span-5">Keyword</span><span className="col-span-3">Target</span><span className="col-span-1">Intent</span><span className="col-span-1 text-center">Pos</span><span className="col-span-1">Status</span><span className="col-span-1 text-right">Actions</span>
        </div>
        {filtered.map((k) => (
          <div key={k.id} data-testid={`kw-row-${k.id}`} className="grid grid-cols-1 items-center gap-2 border-b border-[#F5F1E8] px-4 py-3 last:border-0 md:grid-cols-12 md:gap-3">
            <div className="font-body text-sm font-semibold text-[#2C241B] md:col-span-5">{k.keyword}{k.notes && <span className="block font-normal text-xs text-[#A79C8A]">{k.notes}</span>}</div>
            <div className="truncate font-body text-xs text-[#8A8072] md:col-span-3">{k.target_url || "—"}</div>
            <div className="md:col-span-1"><span className="rounded-full bg-[#F5F1E8] px-2 py-0.5 font-body text-[11px] font-semibold text-[#5C5449]">{k.intent}</span></div>
            <div className="font-body text-sm text-[#5C5449] md:col-span-1 md:text-center">{k.position ?? "—"}</div>
            <div className="md:col-span-1"><Pill status={k.status} /></div>
            <div className="flex gap-1.5 md:col-span-1 md:justify-end">
              <button onClick={() => setForm({ ...EMPTY, ...k, position: k.position ?? "" })} data-testid={`kw-edit-${k.id}`} className="rounded-lg border border-[#E5DFD3] p-1.5 text-[#5C5449] hover:bg-[#F5F1E8]"><Pencil size={13} /></button>
              <button onClick={() => { if (window.confirm(`Delete "${k.keyword}"?`)) act(() => deleteKeyword(k.id), "Keyword deleted"); }} data-testid={`kw-delete-${k.id}`} className="rounded-lg bg-[#FDECEA] p-1.5 text-[#C62828] hover:bg-[#f8dcd8]"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && <div className="px-4 py-16"><Empty>No keywords tracked yet — add the queries you want to win.</Empty></div>}
      </div>

      {form && <KwForm initial={form} onClose={() => setForm(null)} onSaved={load} />}
    </div>
  );
}
